import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyListVehiclesQuery } from "store/services/vehicleApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { BaseTabs } from "components/Shared/BaseTabs";
import ExportVehicleExcel from "./vehicleComponents/ExportVehicleExcel";
import BaseSelect from "components/Shared/Base/BaseSelect";

const VehicleListScreen = () => {
  const [sortByDate, setSortByDate] = React.useState("createdAt:-1");

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Vehicles" }];

  const vehicleListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicle_id,
      cell: (row) => (
        <Link
          title={row?.vehicle_id}
          to={`/app/crm/vehicle/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.vehicle_id ? (
            <HighlightedText text={row?.vehicle_id} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Registration No.",
      selector: (row) => row.registration_number,
      cell: (row) => (
        <Link
          title={row?.registration_number}
          to={`/app/crm/vehicle/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.registration_number ? (
            <HighlightedText
              text={row?.registration_number}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Vehicle Type",
      selector: (row) => row.vehicleType?.name,
      cell: (row) => row.vehicleType?.name,
    },
    {
      name: "Capacity",
      selector: (row) => row.capacity,
      cell: (row) => row?.capacity,
    },
    // {
    //   name: "Insurance ID",
    //   cell: (row) => {
    //     const insurances = Array.isArray(row?.vehicleInsurance)
    //       ? row.vehicleInsurance
    //       : row?.vehicleInsurance
    //       ? [row.vehicleInsurance]
    //       : [];
    //     return (
    //       insurances
    //         .filter((insurance) => insurance?.status)
    //         .map((insurance) => insurance?.insurance_id)
    //         .filter(Boolean)
    //         .join(", ") || "N/A"
    //     );
    //   },
    // },

    // {
    //   name: "Maintenance Due",
    //   selector: (row) => row.maintenance_due_date,
    //   cell: (row) => <AppTableDateTime date={row?.maintenance_due_date} />,
    // },
    {
      name: "Unit",
      selector: (row) => row.unit,
      cell: (row) => row?.unit,
    },
    {
      name: "Ownership",
      selector: (row) => row.owner?.name,
      cell: (row) => row.owner?.name,
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Vehicle" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/vehicle/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Vehicle">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchVehicles, { data: vehicleRes, isLoading, isFetching, error }] =
    useLazyListVehiclesQuery();

  const groupedVehicles = (vehicleRes?.data ?? []).reduce((acc, vehicle) => {
    const { vehicle_id } = vehicle;
    if (!acc[vehicle_id]) {
      acc[vehicle_id] = {
        ...vehicle,
        vehicleInsurance: Array.isArray(vehicle.vehicleInsurance)
          ? vehicle.vehicleInsurance
          : [vehicle.vehicleInsurance],
      };
    } else {
      acc[vehicle_id].vehicleInsurance = [
        ...acc[vehicle_id].vehicleInsurance,
        ...(Array.isArray(vehicle.vehicleInsurance)
          ? vehicle.vehicleInsurance
          : [vehicle.vehicleInsurance]),
      ];
    }
    return acc;
  }, {});

  const groupedVehiclesArray = Object.values(groupedVehicles);

  React.useEffect(() => {
    const [sortedBy, sortDirection] = sortByDate.split(":");
    fetchVehicles({
      Active: activeTab !== "Inactive",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
    });
  }, [activeTab, currentPage, fetchVehicles, keyword, perPage, sortByDate]);

  useAppMeta(`${activeTab} Vehicles`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={vehicleListTabs}
          activeTab={activeTab}
          actions={<ExportVehicleExcel />}
        />

        <TableFilter
          searchLabel="Vehicle ID/Type/Reg.No."
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortByDate}
            onChange={(e) => setSortByDate(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={groupedVehiclesArray}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={vehicleRes?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VehicleListScreen;

const PageActions = () => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Link to="/app/crm/vehicle-listing/list">
        <Button startIcon={<Add />} variant="contained">
          Add External Vehicle
        </Button>
      </Link>
      <Link to="/app/crm/vehicle/create">
        <Button startIcon={<Add />} variant="contained">
          New Vehicle
        </Button>
      </Link>
    </Box>
  );
};